import React, { useMemo, useState } from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import {
  AnyFilterExpressionInput,
  Attribute,
  BooleanOp,
  ByProjectIdReqInput,
  Exact,
  FilterOp,
  MdmDeviceDto,
  MdmObjectDto,
  SortDirection,
  SortInput,
  useUpdateUserSettingsMutation
} from "gql/generated/types";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import useProjectContext from "../hooks/useProjectContext";
import { initialFilter } from "../utils/const";
import { CustomRowData, MdmContainerName, MdmTableName, TableColumn, TableOptions } from "../utils/types";
import { errorToaster, renderCustomFieldValue, successToaster } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

type ObjectAndDeviceTableWrapperProps<T, N> = {
  data: T[];
  tableName: MdmTableName;
  containerName: MdmContainerName;
  columns: TableColumn<T>[];
  loading: boolean;
  setTableOptions: React.Dispatch<React.SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  totalItems: number;
  getQueryData: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const ObjectAndDeviceTableWrapper = <T extends NoInfer<MdmDeviceDto | MdmObjectDto>, N>({ 
  data,
  tableName,
  containerName,
  columns,
  loading,
  setTableOptions,
  tableOptions,
  totalItems,
  getQueryData,
 }: ObjectAndDeviceTableWrapperProps<T, N>) => {
  const { user, userSettings, project, projectId } = useProjectContext();
  const navigate = useNavigate();
  const [customColumns, setCustomColumns] = useState<string[]>(() => {
    let customCol: string[] = [];
    if (tableName === "Devices") {
      customCol = userSettings?.userSettings.deviceTable as string[];
    }
    if (tableName === "Objects") {
      customCol = userSettings?.userSettings.objectTable as string[];
    }
    return customCol.length > 0 ? customCol : columns.map(col => col.columnKey);
  });

  const [updateUserSettings, { loading: updateLoading }] = useUpdateUserSettingsMutation({
    onCompleted: () => successToaster(T("alert.columns-saved-successfully")),
    onError: errorToaster,
  });

  const customCols: Attribute[] = useMemo(() => {
    if (tableName === "Devices") {
      return project?.mdmProjectById.configuration?.deviceConfiguration.customFields as Attribute[] ?? [];
    }
    if (tableName === "Objects") {
      return project?.mdmProjectById.configuration?.objectConfiguration.customFields as Attribute[] ?? [];
    }
    return [];
  }, [
    tableName,
    project?.mdmProjectById.configuration?.deviceConfiguration.customFields,
    project?.mdmProjectById.configuration?.objectConfiguration.customFields,
  ]);

  const mapCol = useMemo(
    () =>
      [
        ...columns,
        ...(customCols?.map(col => ({
          field: `${col?.id}.value`,
          header: T((col?.title ?? "") as StringKey),
          body: (rowData: CustomRowData) =>
            renderCustomFieldValue(rowData, col?.id as keyof CustomRowData, customCols),
          sortable: true,
          columnKey: col?.id,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns, customCols]
  );

  const formatObjectFields = useMemo(
    () =>
      data.reduce((acc, obj) => {
        const objFields = obj.customFields?.reduce((accObj, field) => {
          const newObj = {
            value: field?.value?.value,
            id: field?.key,
            title: customCols.find(col => col?.id === field?.key)?.title,
          };
          return {
            ...accObj,
            [field?.key!]: newObj,
          };
        }, {} as CustomFieldType);

        acc.push({
          ...obj,
          ...objFields,
        });

        return acc;
      }, [] as Partial<T>[]),
    [customCols, data]
  );

  const filteredColumns = useMemo(
    () => mapCol.filter(col => customColumns.includes(col.columnKey)),
    [customColumns, mapCol]
  );

  const handleColumnSave = () => {
    if (tableName === "Devices") {
      updateUserSettings({
        variables: {
          userSettings: {
            id: {
              projectId: projectId!,
              userId: user?.externalId!,
            },
            deviceTable: customColumns,
          },
        },
      });
      return;
    }
    updateUserSettings({
      variables: {
        userSettings: {
          id: {
            projectId: projectId!,
            userId: user?.externalId!,
          },
          objectTable: customColumns,
        },
      },
    });
  };

  const fetchData = async (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
    filter: AnyFilterExpressionInput | null = null,
  ) => {
    const resp = await getQueryData({
      variables: {
        input: {
          projectId,
          take: take ?? tableOptions.rows,
          skip: skip ?? tableOptions.first,
          sort: sort ?? [{
            property: tableOptions.sortField,
            direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? tableOptions.andFilters,
          filter: filter ?? tableOptions.filter,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });

    return resp.data
  };

  const handleSearch = (value: string) => {
    const searchFilter: AnyFilterExpressionInput | null = {
      op: BooleanOp.Or,
      left: {
        op: BooleanOp.Or,
        left: {
          value,
          cond: FilterOp.Contains,
          property:
            tableName === "Devices"
              ? "objectRelation.mdmObject.address.fullAddress"
              : "address.fullAddress",
        },
        right: {
          value,
          cond: FilterOp.Contains,
          property: tableName === "Devices" ? "config.title" : "title",
        },
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "identifier",
      },
    };
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchFilter : null,
      first: 0,
      search: value,
    }));
  };

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "identifier",
      reqSortField: "identifier",
      sortOrder: 1,
    }));
  };
  
  return (
    <LazyTable
      columns={filteredColumns}
      data={formatObjectFields}
      tableName={tableName}
      containerName={containerName}
      loading={loading}
      setTableOptions={setTableOptions}
      tableOptions={tableOptions}
      totalItems={totalItems}
      fetchData={fetchData}
      resetFilters={resetFilters}
      onSearch={handleSearch}
      buttons={
        <>
          <TableColumnSelection
            customColumns={customColumns}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCol}
            loading={updateLoading}
          />
          <Button
            type="button"
            icon="pi pi-plus"
            label={tableName === "Devices" ? T("mdm.add-device") : T("mdm.add-object")}
            onClick={() => navigate(tableName === "Devices" ? "new" : "objects/new")}
          />
        </>
      }
    />
  );
}

export default ObjectAndDeviceTableWrapper;